'use client';

import { usePathname } from 'next/navigation';
import { useAuth } from '@/components/auth-provider';
import { cn } from '@/lib/utils';

interface GuestLandingGradientProps {
  /**
   * When true, render unconditionally (skip pathname + auth guards) and
   * absolutely position inside the closest positioned ancestor. Used by the
   * deck cover slide to inherit the live landing-page aurora exactly.
   * Bypasses the AuthProvider — `useAuth` is still called (to keep hook
   * order stable across the two render paths) but `loading`/`user` are
   * ignored.
   */
  force?: boolean;
}

export function GuestLandingGradient({ force }: GuestLandingGradientProps = {}) {
  const auth = useAuth();
  const pathname = usePathname();
  if (!force && auth.loading) return null;

  if (!force) {
    const isHome = pathname === '/';
    const isPostAuthHero = pathname === '/onboarding';
    // Shareable read-only routes (`/u/<n>`, `/p/<id>`, `/c/<slug>`,
    // `/content/<id>`, `/tag/<n>`, `/marketplace`, `/leaderboard`,
    // `/discover`) all render in the `(public)` shell for guests
    // and should carry the same aurora wash as the landing —
    // otherwise the public chrome (header + footer) sits on a
    // flat dark slab and the visitor can't tell they're on a
    // Robutler surface. Authed visitors never land on these
    // paths (each `page.tsx` server-redirects into the
    // workspace), so we don't need to gate on `auth.user`
    // here — they simply won't see this component.
    const isPublicSurface =
      pathname?.startsWith('/u/') ||
      pathname?.startsWith('/p/') ||
      pathname?.startsWith('/c/') ||
      pathname?.startsWith('/content/') ||
      pathname?.startsWith('/tag/') ||
      pathname === '/marketplace' ||
      pathname?.startsWith('/marketplace/') ||
      pathname === '/leaderboard' ||
      pathname?.startsWith('/leaderboard/') ||
      pathname === '/discover' ||
      pathname?.startsWith('/discover/');
    if (!isHome && !isPostAuthHero && !isPublicSurface) return null;
  }

  // Guest landing is full-bleed; logged-in surfaces (home with an account,
  // /onboarding) leave room for the desktop sidebar. Forced (deck) usage
  // ignores the sidebar gap because it lives in its own canvas. The
  // public shareable surfaces have no sidebar (slim shell) so they
  // count as guest-landing here.
  const isGuestLanding =
    force ||
    (pathname === '/' && !auth.user) ||
    pathname?.startsWith('/u/') === true ||
    pathname?.startsWith('/p/') === true ||
    pathname?.startsWith('/c/') === true ||
    pathname?.startsWith('/content/') === true ||
    pathname?.startsWith('/tag/') === true ||
    pathname === '/marketplace' ||
    pathname?.startsWith('/marketplace/') === true ||
    pathname === '/leaderboard' ||
    pathname?.startsWith('/leaderboard/') === true;

  return (
    <div
      aria-hidden
      className={cn(
        'hero-gradient-stage pointer-events-none z-0 overflow-hidden @container/home',
        // Forced mode pins to its parent; the natural call site uses fixed
        // viewport coords for the global page-level aurora.
        force ? 'absolute inset-0' : 'fixed inset-y-0 right-0',
        // Logged-in/loading home keeps the glow in the main column so it does
        // not bleed under the app sidebars. Guest landing has no sidebars.
        force ? '' : isGuestLanding ? 'left-0' : 'left-0 md:left-64',
      )}
    >
      <div className="absolute left-1/2 top-[31%] h-[56vh] w-[92vw] -translate-x-1/2 -translate-y-1/2 @md/home:top-[34%] @md/home:h-[62vh] @md/home:w-[78vw]">
        <div className="hero-aurora-field" />
      </div>
      <div className="absolute left-1/2 top-[30%] @md/home:top-[34%]">
        <div
          className="hero-orb hero-orb-a mix-blend-multiply dark:mix-blend-screen w-[88vw] h-[54vh] @md/home:w-[50vw] @md/home:h-[54vh]"
          style={{
            background:
              'radial-gradient(closest-side, rgba(172,85,255,0.82), rgba(172,85,255,0) 68%)',
          }}
        />
        <div
          className="hero-orb hero-orb-b mix-blend-multiply dark:mix-blend-screen w-[60vw] h-[38vh] @md/home:w-[36vw] @md/home:h-[40vh]"
          style={{
            background:
              'radial-gradient(closest-side, rgba(57,208,255,0.74), rgba(57,208,255,0) 68%)',
          }}
        />
        <div
          className="hero-orb hero-orb-c mix-blend-multiply dark:mix-blend-screen w-[60vw] h-[38vh] @md/home:w-[36vw] @md/home:h-[40vh]"
          style={{
            background:
              'radial-gradient(closest-side, rgba(249,122,237,0.74), rgba(249,122,237,0) 68%)',
          }}
        />
        <div
          className="hero-orb hero-orb-d mix-blend-multiply dark:mix-blend-screen w-[54vw] h-[32vh] @md/home:w-[30vw] @md/home:h-[34vh]"
          style={{
            background:
              'radial-gradient(closest-side, rgba(5,206,133,0.62), rgba(5,206,133,0) 68%)',
          }}
        />
        <div
          className="hero-orb hero-orb-e mix-blend-multiply dark:mix-blend-screen w-[50vw] h-[30vh] @md/home:w-[28vw] @md/home:h-[32vh]"
          style={{
            background:
              'radial-gradient(closest-side, rgba(255,99,44,0.64), rgba(240,237,0,0.30) 48%, rgba(255,99,44,0) 72%)',
          }}
        />
        <div
          className="hero-orb hero-orb-f mix-blend-multiply dark:mix-blend-screen w-[46vw] h-[28vh] @md/home:w-[26vw] @md/home:h-[30vh]"
          style={{
            background:
              'radial-gradient(closest-side, rgba(240,237,0,0.46), rgba(240,237,0,0) 66%)',
          }}
        />
        <div
          className="hero-orb hero-orb-g mix-blend-multiply dark:mix-blend-screen w-[50vw] h-[30vh] @md/home:w-[30vw] @md/home:h-[32vh]"
          style={{
            background:
              'radial-gradient(closest-side, rgba(57,208,255,0.48), rgba(5,206,133,0.28) 45%, rgba(57,208,255,0) 70%)',
          }}
        />
      </div>
      <div className="hidden dark:block absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_30%,transparent_0%,rgba(0,0,0,0.28)_100%)]" />
    </div>
  );
}
